import { z } from 'zod';

const DEFAULT_API_BASE_PATH = '/api';

// ! WARNING: public configuration properties are exposed in the browser.
// ! Only enable this for entries which are safe to be public. NO SECRETS.
const configFields = {
  // TODO validate non-optional entries for staging/production, e.g. matomo, sentry, mailchimp.
  APP_VERSION: { schema: z.string().nonempty(), public: true },

  DEPLOYMENT_ENV: {
    schema: z.enum(['local', 'preview', 'development', 'staging', 'production']),
    public: true,
  },

  KEYCLOAK_CLIENT_ID: { schema: z.string().nonempty(), public: false },
  KEYCLOAK_CLIENT_SECRET: { schema: z.string().nonempty(), public: false },
  KEYCLOAK_ISSUER: { schema: z.url(), public: false },

  NEXTAUTH_SECRET: { schema: z.string().nonempty(), public: false },
  AUTH_PROXY_URL: { schema: z.url().optional(), public: true },

  GRADING_WEB_LAUNCH_HMAC_SECRET: { schema: z.string().optional(), public: false },

  MAILCHIMP_API_KEY: {
    schema: z.string().nonempty().optional(),
    public: false,
  },
  MAILCHIMP_API_SERVER: {
    schema: z.string().nonempty().optional(),
    public: false,
  },
  MAILCHIMP_AUDIENCE_ID: {
    schema: z.string().nonempty().optional(),
    public: false,
  },

  GITHUB_FEEDBACK_TOKEN: { schema: z.string().optional(), public: false },
  GITHUB_FEEDBACK_PROJECT_ID: { schema: z.string().optional(), public: false },

  API_ORIGIN: { schema: z.url().optional(), public: true },

  AI_AGENT_URL: { schema: z.url().optional(), public: true },
  AUTH_MANAGER_URL: { schema: z.url().optional(), public: true },
  CELL_API_URL: { schema: z.url().optional(), public: true },
  ENTITY_CORE_URL: { schema: z.url().optional(), public: true },
  NOTEBOOK_API_URL: { schema: z.string().optional(), public: true },
  OBI_ONE_URL: { schema: z.url().optional(), public: true },
  SMALL_SCALE_SIMULATOR_URL: {
    schema: z.url().optional(),
    public: true,
  },
  THUMBNAIL_API_URL: { schema: z.url().optional(), public: true },
  VIRTUAL_LAB_API_URL: { schema: z.url().optional(), public: true },
  LAUNCH_SYSTEM_URL: { schema: z.url().optional(), public: true },
  ROOT_ROUTE: { schema: z.string(), public: true },

  SENTRY_DSN: {
    schema: z.preprocess((sentryDsn) => sentryDsn || undefined, z.url().optional()),
    public: true,
  },
  SENTRY_ORG: { schema: z.string().optional(), public: true },
  SENTRY_PRJ: { schema: z.string().optional(), public: true },

  STRIPE_PUBLISHABLE_KEY: {
    schema: z.string().startsWith('pk_'),
    public: true,
  },

  MATOMO_CDN_URL: { schema: z.string().nonempty().optional(), public: true },
  MATOMO_SITE_ID: { schema: z.string().nonempty().optional(), public: true },
  MATOMO_URL: { schema: z.string().nonempty().optional(), public: true },

  SANITY_PROJECT_ID: { schema: z.string().nonempty(), public: true },
  SANITY_DATASET: { schema: z.enum(['staging', 'production']), public: true },

  ENTITY_CORE_PUBLIC_PROJECT_ID: {
    schema: z.string().nonempty(),
    public: true,
  },
  ENTITY_CORE_PUBLIC_VIRTUAL_LAB_ID: {
    schema: z.string().nonempty(),
    public: true,
  },
  APP_DEFAULT__BRAIN_REGION_HIERARCHY_ID: {
    schema: z.string().nonempty(),
    public: true,
  },
  MOUSE_ATLAS__ID: { schema: z.string().nonempty(), public: true },
  MOUSE_DEFAULT__SELECTED_BRAIN_REGION_ID: {
    schema: z.string().nonempty(),
    public: true,
  },
  HUMAN_DEFAULT__SELECTED_BRAIN_REGION_ID: {
    schema: z.string().nonempty(),
    public: true,
  },
  RAT_DEFAULT__SELECTED_BRAIN_REGION_ID: {
    schema: z.string().nonempty(),
    public: true,
  },
  EXCLUDED_HIERARCHY_IDS: {
    schema: z.preprocess((val) => {
      if (typeof val === 'string') {
        return val
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);
      }
      return val;
    }, z.array(z.string())),
    public: true,
  },
  LEGACY_DEFAULT_CIRCUIT_ID: {
    schema: z.url().nonempty(),
    public: true,
  },

  NOTEBOOK_REPO_URL: { schema: z.url(), public: true },
} as const;

const platformApiUrlFields = {
  AI_AGENT_URL: '/agent-ts/api',
  AUTH_MANAGER_URL: '/auth-manager/v1',
  CELL_API_URL: '/circuit',
  ENTITY_CORE_URL: '/entitycore',
  NOTEBOOK_API_URL: '/notebook_service',
  OBI_ONE_URL: '/obi-one',
  SMALL_SCALE_SIMULATOR_URL: '/small-scale-simulator',
  THUMBNAIL_API_URL: '/thumbnail-generation',
  VIRTUAL_LAB_API_URL: '/virtual-lab-manager',
  LAUNCH_SYSTEM_URL: '/launch-system',
} as const satisfies Partial<Record<keyof typeof configFields, string>>;

const baseServerSchema = z
  .object(
    Object.fromEntries(Object.entries(configFields).map(([key, { schema }]) => [key, schema]))
  )
  .superRefine((data, ctx) => {
    if (data.AUTH_PROXY_URL && data.DEPLOYMENT_ENV !== 'preview') {
      ctx.addIssue({
        code: 'custom',
        message: 'AUTH_PROXY_URL can only be set when DEPLOYMENT_ENV is "preview"',
        path: ['AUTH_PROXY_URL'],
      });
    }
  }) as any as z.ZodObject<{
  [K in keyof typeof configFields]: (typeof configFields)[K]['schema'];
}>;

// biome-ignore lint/suspicious/noExplicitAny: reason for using any
const applyApiUrlTransforms = <T extends z.ZodObject<any>>(schema: T) =>
  schema
    .superRefine((data, ctx) => {
      Object.keys(platformApiUrlFields).forEach((field) => {
        if (!data[field] && !data.API_ORIGIN) {
          ctx.addIssue({
            code: 'custom',
            message: `Either ${field} or API_ORIGIN must be provided`,
            path: [field],
          });
        }
      });
    })
    .transform((data) => ({
      ...data,
      ...Object.fromEntries(
        Object.entries(platformApiUrlFields).map(([field, path]) => [
          field,
          data[field] ?? `${data.API_ORIGIN}${DEFAULT_API_BASE_PATH}${path}`,
        ])
      ),
    }));

export const serverSchema = applyApiUrlTransforms(baseServerSchema);

export const baseClientSchema = z.object(
  Object.fromEntries(
    Object.entries(configFields)
      .filter(([, { public: isPublic }]) => isPublic)
      .map(([key, { schema }]) => [key, schema])
  )
) as z.ZodObject<{
  [K in keyof typeof configFields as (typeof configFields)[K]['public'] extends true
    ? K
    : never]: (typeof configFields)[K]['schema'];
}>;

export const clientSchema = applyApiUrlTransforms(baseClientSchema);

export type ServerConfig = z.infer<typeof serverSchema>;
export type ClientConfig = z.infer<typeof clientSchema>;
